import React, { useEffect } from 'react'
import './home.css'
import './apps.css'
import background from './backgroundweb.jpeg'
import { Link } from 'react-router-dom'
import { APPS } from '../Apps/apps'
import Mission from './Mission'
import Artists from './Artists'
import Footer from './Footer'

const AppCard = ({ app, user, loginClick }) => {
  const Icon = app.icon
  const studioAction = <Link to="/transformation" className="appBtn">{app.cta}</Link>
  const action = app.inApp
    ? studioAction
    : <a href={app.link} target="_blank" rel="noopener noreferrer" className="appBtn">{app.cta} <span className="extArrow" aria-hidden="true">&#8599;</span></a>

  let tileContent = null
  if (app.avatar) {
    tileContent = (
      <img
        src={app.avatar}
        alt={`${app.name} avatar`}
        className={`appAvatar appAvatar--${app.id}`}
        loading="lazy"
      />
    )
  } else if (Icon) {
    tileContent = <Icon className="appTileIcon" />
  }

  let handleElement = null
  if (app.handle) {
    if (app.link) {
      handleElement = (
        <a
          href={app.link}
          target="_blank"
          rel="noopener noreferrer"
          className="appHandle"
        >
          {app.handle}
        </a>
      )
    } else {
      handleElement = <span className="appHandle">{app.handle}</span>
    }
  }

  const cardTile = app.micrositeUrl
    ? (
      <Link to={app.micrositeUrl} title={`Explore ${app.name} Guide`}>
        {tileContent}
      </Link>
      )
    : tileContent

  const cardTitle = app.micrositeUrl
    ? (
      <Link to={app.micrositeUrl} className="appTitleLink">
        {app.name}
      </Link>
      )
    : app.name

  return (
    <div className={`appCard appCard--${app.id}`}>
      <div className="appCardHeader">
        <div className={`appTile appTile--${app.id}`}>
          {cardTile}
        </div>
        <div className="appCardTitle">
          <h3>{cardTitle}</h3>
          <span className="appBadge">{app.badge}</span>
        </div>
      </div>
      <p className="appTagline">{app.tagline}</p>
      <p className="appDesc">{app.description}</p>
      <ul className="appFeatures">
        {app.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {app.accessNote && (
        <div className="appAccessNote">
          <span className="accessIcon" aria-hidden="true">✨</span>
          <span>{app.accessNote}</span>
        </div>
      )}
      <div className="appCardFooter">
        {action}
        {app.micrositeUrl && (
          <Link to={app.micrositeUrl} className="appMicrositeLink">
            <span>Explore Agent Guide &amp; Prompts</span>
            <span aria-hidden="true">&rarr;</span>
          </Link>
        )}
        {handleElement}
        {!app.inApp && (
          <span className="telegramHint">
            Opens in Telegram (App or Web) · Free &amp; Private
          </span>
        )}
      </div>
    </div>
  )
}

const ClientApp = React.forwardRef(function client ({ user, loginClick }, ref) {
  // When arriving on the home page with a #apps, #mission, or #artists hash, scroll smoothly
  useEffect(() => {
    const hash = window.location.hash
    if (hash === '#apps' || hash === '#mission' || hash === '#artists') {
      const t = setTimeout(() => {
        const el = document.querySelector(hash)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 200)
      return () => clearTimeout(t)
    }
    return undefined
  }, [])

  return (
    <div className='mainpage' style={{ width: '100%' }}>
      {/* Hero */}
      <div className="backgroundContainer hero" ref={ref}>
        <img src={background} className="background heroBanner"/>
        <div className="heroContent">
          <div className="heroKicker">The Dionyziuz suite</div>
          <h1 className="heroH1">AI for the things you love</h1>
          <p className="heroSub">
            Dionyziuz builds specialized artificial intelligence — for the water,
            the high country, the silver screen, the chessboard, and the canvas.
          </p>
          <div className="heroCtas">
            <a href="#apps" className="heroBtn heroBtnPrimary">Meet the agents</a>
            <Link to="/transformation" className="heroBtn heroBtnGhost">Try the art studio</Link>
          </div>
          <div className="heroMicrositeLinks">
            <span className="heroMicrositeLabel">Agent Deep Dives:</span>
            <Link to="/wind" className="heroMicrositePill">💨 Windsports</Link>
            <Link to="/sierra" className="heroMicrositePill">🏔️ Sierra</Link>
            <Link to="/media" className="heroMicrositePill">🎬 Movies</Link>
            <Link to="/tichess" className="heroMicrositePill">♟️ TiChess</Link>
          </div>
        </div>
      </div>

      {/* Agents */}
      <div className="appsSection" id="apps">
        <div className="container">
          <h2>Meet the agents</h2>
          <p className="appsSectionSub">
            Four specialized AI agents on Telegram paired with our flagship creative studio that lives right here.
            Built on a simple conviction: talking to an agent is more flexible, fluid, and powerful than navigating static apps.
            <a href="#mission" className="appsMissionLink">Read our mission &darr;</a>
          </p>
          <div className="micrositeQuickBar">
            <span className="quickBarLabel">Explore Dedicated Microsites:</span>
            <div className="quickBarLinks">
              <Link to="/wind" className="quickBarPill quickBarPill--wind">💨 Windsports &rarr;</Link>
              <Link to="/sierra" className="quickBarPill quickBarPill--sierra">🏔️ Sierra Guide &rarr;</Link>
              <Link to="/media" className="quickBarPill quickBarPill--media">🎬 Movies &amp; Series &rarr;</Link>
              <Link to="/tichess" className="quickBarPill quickBarPill--chess">♟️ TiChess &rarr;</Link>
            </div>
          </div>
          <div className="appsPreviewNotice">
            <div className="noticeHeader">
              <span className="noticeBadge">Instant Try-Out</span>
              <span className="noticeTagline">10 Questions to Test · Free Ongoing Access</span>
            </div>
            <div className="noticeBody">
              <h3 className="noticeTitle">Try out any agent instantly with 10 test questions</h3>
              <p className="noticeText">
                Experience any agent instantly in Telegram — no sign-up or waitlist required to test them out. You get <strong>10 instant try-out questions per agent</strong> to explore real-time spot forecasts, backcountry trail and permit intel, cinema and streaming concierges, or grandmaster chess sparring. When you reach 10 questions, request approval with one tap to unlock ongoing access — <strong>continued access after approval is completely free</strong>.
              </p>
              <div className="noticeTip">
                <strong>Self-learning companion:</strong> All agents adapt directly from your feedback. If a response ever misses what you wanted, reply: <em>&ldquo;I expected you to...&rdquo;</em> or <em>&ldquo;Next time, please...&rdquo;</em> and the agent turns your note into permanent procedural memory.
              </div>
            </div>
          </div>
          <div className="appsGrid">
            {APPS.map((app) => (
              <AppCard key={app.id} app={app} user={user} loginClick={loginClick}/>
            ))}
          </div>

          {/* Telegram Guide & Safety/TOC Section */}
          <div className="telegramGuideCard">
            <div className="guideHeader">
              <span className="guideBadge">Quick Guide</span>
              <h3 className="guideTitle">What happens when you click &ldquo;Chat with...&rdquo;?</h3>
              <p className="guideSub">
                Our agents live on Telegram — the world&apos;s fastest, most privacy-conscious messaging network. Here is how easy it is to get started:
              </p>
            </div>
            <div className="guideSteps">
              <div className="guideStep">
                <div className="stepIconWrapper" aria-hidden="true">1</div>
                <div className="stepBody">
                  <h4>Opens Telegram (App or In-Browser)</h4>
                  <p>
                    Clicking any agent opens its official chat link. If you already have Telegram, it launches instantly. If not, you can <a href="https://telegram.org" target="_blank" rel="noopener noreferrer">download Telegram free</a> for any device (iOS, Android, Mac, Windows, Linux) or use <strong>Telegram Web</strong> directly in your browser without installing anything.
                  </p>
                </div>
              </div>
              <div className="guideStep">
                <div className="stepIconWrapper" aria-hidden="true">2</div>
                <div className="stepBody">
                  <h4>Tap &ldquo;Start&rdquo; for 10 Try-Out Questions</h4>
                  <p>
                    Every agent gives you <strong>10 instant try-out questions</strong> with zero sign-up or waitlist required. Put each agent through real-world scenarios: spot forecasts, trail permits, streaming availability, or chess moves. Continued access after 1-tap approval is completely free.
                  </p>
                </div>
              </div>
              <div className="guideStep">
                <div className="stepIconWrapper" aria-hidden="true">3</div>
                <div className="stepBody">
                  <h4>Safe, Private &amp; Built for Agents</h4>
                  <p>
                    Telegram operates on encrypted cloud infrastructure with zero third-party tracking or ad-profiling. Review Telegram&apos;s official <a href="https://telegram.org/tos" target="_blank" rel="noopener noreferrer">Terms of Service (TOC)</a> and <a href="https://telegram.org/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                  </p>
                </div>
              </div>
            </div>
            <div className="guideMicrositeCallout">
              <span className="calloutTitle">Want to explore full documentation, prompt ideas &amp; commands?</span>
              <div className="calloutPills">
                <Link to="/wind" className="calloutPill">💨 Windsports Guide &rarr;</Link>
                <Link to="/sierra" className="calloutPill">🏔️ Sierra Guide &rarr;</Link>
                <Link to="/media" className="calloutPill">🎬 Movies &amp; Series Guide &rarr;</Link>
                <Link to="/tichess" className="calloutPill">♟️ TiChess Guide &rarr;</Link>
              </div>
            </div>
            <div className="trustFooter">
              <div className="trustBadges">
                <span className="trustBadgeItem">🔒 End-to-end encrypted protocols</span>
                <span className="trustBadgeItem">⚡ Instant cross-device sync</span>
                <span className="trustBadgeItem">🚫 Zero ad tracking</span>
              </div>
              <div className="trustLinks">
                <a href="https://telegram.org/tos" target="_blank" rel="noopener noreferrer">Telegram Terms of Service &rarr;</a>
                <a href="https://telegram.org/privacy" target="_blank" rel="noopener noreferrer">Telegram Privacy Policy &rarr;</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission: Why Agents Instead of Apps */}
      <Mission />

      {/* Our favorite artists */}
      <Artists user={user} loginClick={loginClick} />

      {/* Modern Footer */}
      <Footer user={user} loginClick={loginClick} />
  </div>
  )
})

export default ClientApp
